import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const INK    = '#0d0d0d';
const BLUE   = '#1755f4';
const MUTED  = '#888';
const BORDER = '#e8e8e8';

const TYPE_LABEL  = { maintenance: 'Reparatur', document: 'Dokument', general: 'Allgemein' };
const TYPE_COLOR  = { maintenance: '#dc2626', document: '#111', general: '#888' };
const STATUS_OPT  = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_LABEL = { open: 'Offen', in_progress: 'In Bearbeitung', resolved: 'Erledigt', closed: 'Geschlossen' };
const STATUS_BADGE = { open: 'badge-red', in_progress: 'badge-yellow', resolved: 'badge-green', closed: 'badge-gray' };

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Requests() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('open');
  const [updating, setUpdating] = useState(null);

  useEffect(() => { fetchRequests(); }, []);

  async function fetchRequests() {
    const { data, error } = await supabase
      .from('tenant_requests')
      .select('*, tenants(first_name, last_name, unit, property_id, properties:property_id(name))')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });
    if (!error) setRequests(data || []);
    setLoading(false);
  }

  async function updateStatus(id, status) {
    setUpdating(id);
    const { error } = await supabase
      .from('tenant_requests')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('owner_id', user.id);
    if (error) { addToast('Fehler beim Aktualisieren.', 'error'); }
    else {
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      addToast('Status aktualisiert.', 'success');
    }
    setUpdating(null);
  }

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  const counts = {
    open:        requests.filter(r => r.status === 'open').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    resolved:    requests.filter(r => r.status === 'resolved').length,
    all:         requests.length,
  };

  return (
    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: INK }}>

      {/* Header */}
      <div style={{ marginBottom: isMobile ? '24px' : '40px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '8px' }}>Mieterportal</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 900, letterSpacing: '-1.5px', margin: 0 }}>
            Anfragen
          </h1>
          {!loading && (
            <span style={{ fontSize: '13px', color: MUTED }}>
              {counts.open} offen
            </span>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0', border: `1px solid ${BORDER}`, background: 'white', marginBottom: '24px', width: 'fit-content' }}>
        {[
          { key: 'open',        label: `Offen (${counts.open})` },
          { key: 'in_progress', label: `In Bearbeitung (${counts.in_progress})` },
          { key: 'resolved',    label: `Erledigt (${counts.resolved})` },
          { key: 'all',         label: `Alle (${counts.all})` },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: '9px 16px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px',
              border: 'none', borderRight: `1px solid ${BORDER}`, cursor: 'pointer',
              background: filter === key ? INK : 'white',
              color: filter === key ? 'white' : MUTED,
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '64px', textAlign: 'center', border: `1px solid ${BORDER}`, background: 'white' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED }}>Lädt...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '64px', textAlign: 'center', border: `1px solid ${BORDER}`, background: 'white' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED }}>
            Keine Anfragen
          </span>
        </div>
      ) : isMobile ? (
        /* Mobile cards */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filtered.map(r => (
            <div key={r.id} style={{ border: `1px solid ${BORDER}`, background: 'white', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700 }}>{r.title}</div>
                <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
              </div>
              <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>
                {r.tenants?.last_name}, {r.tenants?.first_name} · {r.tenants?.unit || '–'}
              </div>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', padding: '2px 7px', background: TYPE_COLOR[r.type] + '18', color: TYPE_COLOR[r.type] }}>
                  {TYPE_LABEL[r.type]}
                </span>
                <span style={{ fontSize: '11px', color: MUTED }}>{fmt(r.created_at)}</span>
              </div>
              {r.description && (
                <p style={{ fontSize: '12px', color: MUTED, lineHeight: '1.5', margin: '0 0 10px' }}>{r.description}</p>
              )}
              <select
                value={r.status}
                onChange={e => updateStatus(r.id, e.target.value)}
                disabled={updating === r.id}
                style={{ width: '100%', border: `1px solid ${BORDER}`, padding: '8px 10px', fontSize: '12px', color: INK, fontFamily: 'inherit', background: 'white' }}
              >
                {STATUS_OPT.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop table */
        <div style={{ border: `1px solid ${BORDER}`, background: 'white' }}>
          <div className="immo-thead" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 140px', padding: '12px 28px', borderBottom: `1px solid ${BORDER}` }}>
            {['Betreff', 'Mieter', 'Typ', 'Datum', 'Status', 'Aktion'].map(h => (
              <span key={h} style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED }}>{h}</span>
            ))}
          </div>
          {filtered.map((r, i) => (
            <div key={r.id} className="immo-row" style={{
              display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 140px',
              padding: '18px 28px', alignItems: 'center',
              borderBottom: i < filtered.length - 1 ? `1px solid ${BORDER}` : 'none',
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{r.title}</div>
                {r.description && (
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>
                    {r.description}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{r.tenants?.last_name}, {r.tenants?.first_name}</div>
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>
                  {r.tenants?.properties?.name} {r.tenants?.unit ? `· ${r.tenants.unit}` : ''}
                </div>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', background: TYPE_COLOR[r.type] + '18', color: TYPE_COLOR[r.type] }}>
                {TYPE_LABEL[r.type]}
              </span>
              <div style={{ fontSize: '12px', color: MUTED }}>{fmt(r.created_at)}</div>
              <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
              <select
                value={r.status}
                onChange={e => updateStatus(r.id, e.target.value)}
                disabled={updating === r.id}
                style={{ border: `1px solid ${BORDER}`, padding: '6px 8px', fontSize: '11px', color: INK, fontFamily: 'inherit', background: 'white', cursor: 'pointer' }}
              >
                {STATUS_OPT.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
